import Root from '@material-ui/core/Accordion';
import Summary from '@material-ui/core/AccordionSummary';
import Details from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { ChangeEvent, ReactNode, useState } from 'react';
import PlainArrowIcon from '../../theme/styled-components/PlainArrowIcon';

interface MakeStylesProps {
  contentPadding: string | number;
  fill: boolean;
  highlight?: string;
  shadow: boolean;
}

const useStyles = makeStyles<Theme, MakeStylesProps>((theme) => ({
  root: ({ fill, shadow }) => ({
    background: 'transparent',
    borderRadius: 5,
    boxShadow:
      shadow && fill
        ? `0 0 8px 0 ${theme.palette.common.backgroundDark}`
        : 'none',
    margin: '10px 0',
    '&$root': {
      borderRadius: 5,
    },
    '&.Mui-expanded': {
      margin: '10px 0',
    },
  }),
  details: ({ contentPadding, fill }) => ({
    background: fill ? theme.palette.common.backgroundHighlight : 'transparent',
    borderRadius: fill ? '0 0 5px 5px' : 0,
    padding: contentPadding,
  }),
  icon: {
    transform: 'rotate(180deg) !important',
  },
  summary: ({ fill, highlight, shadow }) => ({
    background: theme.palette.common.backgroundHighlight,
    color: theme.palette.common.tan,
    borderLeft: highlight ? `5px solid ${highlight}` : 0,
    borderRadius: 5,
    boxShadow:
      shadow && !fill
        ? `0 0 8px 0 ${theme.palette.common.backgroundDark}`
        : 'none',
    '& .MuiAccordionSummary-content': {
      borderRight: `1px solid ${theme.palette.common.backgroundDark}`,
      margin: '25px 0',
      paddingLeft: 24,
      paddingRight: 24,
    },
    '&.Mui-expanded': {
      borderBottom: fill
        ? `1px solid ${theme.palette.common.backgroundNotification}`
        : 0,
      borderRadius: fill ? '5px 5px 0 0' : 5,
      color: theme.palette.common.coral,
    },
  }),
  title: {
    color: 'inherit',
    fontSize: theme.typography.pxToRem(18),
  },
}));

interface AccordionItemProps {
  /** Change event handler */
  change: (event: ChangeEvent<unknown>, isExpanded: boolean) => void;

  /** Item content */
  children: ReactNode;

  /** Padding for expanded content */
  contentPadding: string | number;

  /** Flag to show if item is expanded or not */
  expanded: boolean;

  /** Whether expanded view should have a background colour set */
  fill: boolean;

  /** Highlight colour to display */
  highlight?: string;

  /** Unique item ID */
  id: string;

  /** Display box shadow */
  shadow: boolean;

  /** Item title */
  title: string | ReactNode;

  /** Flag whether this item will toggle the accordion */
  toggle: boolean;
}

function AccordionItem({
  change,
  children,
  contentPadding,
  expanded,
  fill,
  highlight,
  id,
  shadow,
  title,
  toggle,
}: AccordionItemProps) {
  const classes = useStyles({
    contentPadding,
    fill,
    highlight,
    shadow,
  });

  const ariaControls = `${id}-content`;

  const header = `${id}-header`;

  return (
    <Root
      className={classes.root}
      expanded={expanded}
      onChange={change}
      data-testid="accordion-item"
    >
      <Summary
        id={header}
        className={classes.summary}
        aria-controls={ariaControls}
        data-testid={`accordion-summary-${id}`}
        expandIcon={
          <PlainArrowIcon
            $point={toggle ? 'down' : 'left'}
            width="18px"
            height="18px"
          />
        }
      >
        {typeof title === 'string' ? (
          <Typography className={classes.title}>{title}</Typography>
        ) : (
          title
        )}
      </Summary>
      <Details
        className={classes.details}
        data-testid={`accordion-details-${id}`}
      >
        {children}
      </Details>
    </Root>
  );
}

interface AccordionProps {
  /** Items to show in the accordion */
  items: {
    /** Content to display */
    content: ReactNode;

    /** Padding for expanded content. Defaults to 40px */
    contentPadding?: string | number;

    /** Whether expanded view should have a background colour set. Defaults to true */
    fill?: boolean;

    /** Highlight colour to display */
    highlight?: string;

    /** Unique item ID */
    id: string;

    onClick?: () => void;

    /** Display box shadow. Defaults to false */
    shadow?: boolean;

    /** Content title */
    title: string | ReactNode;
  }[];
}

function Accordion({ items }: AccordionProps) {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (id: string) => (_: ChangeEvent<unknown>, isExpanded: boolean) => {
      setExpanded(isExpanded ? id : false);
    };

  return (
    <>
      {items.map((item) => (
        <AccordionItem
          id={item.id}
          key={item.id}
          title={item.title}
          expanded={expanded === item.id}
          contentPadding={item.contentPadding ?? 40}
          fill={item.fill ?? true}
          highlight={item.highlight}
          shadow={item.shadow ?? false}
          toggle={!item.onClick}
          change={item.onClick ? item.onClick : handleChange(item.id)}
        >
          {item.content}
        </AccordionItem>
      ))}
    </>
  );
}

export default Accordion;
